"use client";

import { useState, useEffect, useRef, useCallback } from "react";

export default function PropertyGallery({
    images = [],
    baseUrl = "",
    title = "",
    onViewAll,
}) {
    const count = images.length;
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    // Touch / swipe state
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);
    const touchStartY = useRef(0);
    const isDragging = useRef(false);
    const dragOffset = useRef(0);
    const [currentOffset, setCurrentOffset] = useState(0);

    // ── helpers ──────────────────────────────────────────
    const openLightbox = (index) => {
        setActiveIndex(index);
        setLightboxOpen(true);
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        setCurrentOffset(0);
    };

    const goNext = useCallback(() => {
        setActiveIndex((prev) => (prev + 1) % count);
        setCurrentOffset(0);
    }, [count]);

    const goPrev = useCallback(() => {
        setActiveIndex((prev) => (prev - 1 + count) % count);
        setCurrentOffset(0);
    }, [count]);

    // ── keyboard navigation ─────────────────────────────
    useEffect(() => {
        if (!lightboxOpen) return;

        const handleKey = (e) => {
            if (e.key === "ArrowRight") goNext();
            else if (e.key === "ArrowLeft") goPrev();
            else if (e.key === "Escape") closeLightbox();
        };

        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [lightboxOpen, goNext, goPrev]);

    // ── lock body scroll when lightbox is open ──────────
    useEffect(() => {
        if (lightboxOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [lightboxOpen]);

    // ── touch handlers ──────────────────────────────────
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
        isDragging.current = true;
        dragOffset.current = 0;
    };

    const handleTouchMove = (e) => {
        if (!isDragging.current) return;
        const diffX = e.touches[0].clientX - touchStartX.current;
        const diffY = Math.abs(e.touches[0].clientY - touchStartY.current);
        // Only track horizontal movement
        if (diffY < Math.abs(diffX)) {
            dragOffset.current = diffX;
            setCurrentOffset(diffX);
        }
    };

    const handleTouchEnd = () => {
        isDragging.current = false;
        const threshold = 50;
        if (dragOffset.current < -threshold) {
            goNext();
        } else if (dragOffset.current > threshold) {
            goPrev();
        } else {
            setCurrentOffset(0);
        }
        dragOffset.current = 0;
    };

    if (!images || count === 0) return null;

    // Build full src
    const src = (img) => (img.startsWith("http") ? img : baseUrl + img);

    return (
        <>
            <div className="mb-6 w-full h-[400px] overflow-hidden rounded-xl">
                {/* ===== 1 IMAGE ===== */}
                {count === 1 && (
                    <div className="w-full h-full cursor-pointer" onClick={() => openLightbox(0)}>
                        <img
                            src={src(images[0])}
                            className="w-full h-full object-cover"
                            alt={title || "Property image"}
                        />
                    </div>
                )}

                {/* ===== 2 IMAGES ===== */}
                {count === 2 && (
                    <div className="flex gap-2 h-full">
                        {images.slice(0, 2).map((img, i) => (
                            <img
                                key={i}
                                src={src(img)}
                                className="w-1/2 h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                alt={title || "Property image"}
                                onClick={() => openLightbox(i)}
                            />
                        ))}
                    </div>
                )}

                {/* ===== 3 IMAGES ===== */}
                {count === 3 && (
                    <div className="flex gap-2 h-full">
                        <div className="w-1/2 h-full cursor-pointer" onClick={() => openLightbox(0)}>
                            <img
                                src={src(images[0])}
                                className="w-full h-full object-cover"
                                alt={title || "Property image"}
                            />
                        </div>
                        <div className="w-1/2 h-full flex flex-col gap-2">
                            {images.slice(1, 3).map((img, i) => (
                                <div key={i} className="flex-1 min-h-0 cursor-pointer overflow-hidden" onClick={() => openLightbox(i + 1)}>
                                    <img
                                        src={src(img)}
                                        className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                                        alt={title || "Property image"}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ===== MORE THAN 3 ===== */}
                {count > 3 && (
                    <div className="flex gap-2 h-full">
                        <div className="w-1/2 h-full cursor-pointer" onClick={() => openLightbox(0)}>
                            <img
                                src={src(images[0])}
                                className="w-full h-full object-cover"
                                alt={title || "Property image"}
                            />
                        </div>
                        <div className="w-1/2 h-full flex flex-col gap-2">
                            <div className="flex-1 min-h-0 cursor-pointer overflow-hidden" onClick={() => openLightbox(1)}>
                                <img
                                    src={src(images[1])}
                                    className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                                    alt={title || "Property image"}
                                />
                            </div>
                            <div className="flex-1 min-h-0 relative cursor-pointer overflow-hidden" onClick={() => openLightbox(2)}>
                                <img
                                    src={src(images[2])}
                                    className="w-full h-full object-cover"
                                    alt={title || "Property image"}
                                />
                                <div className="absolute inset-0 bg-black/50 text-white font-semibold text-lg flex items-center justify-center hover:bg-black/40 transition-colors">
                                    +{count - 3} Photos
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ===== LIGHTBOX POPUP ===== */}
            {lightboxOpen && (
                <div
                    className="fixed inset-0 z-[9999] flex flex-col"
                    style={{ backgroundColor: "rgba(0,0,0,0.95)" }}
                >
                    {/* ── Top Bar ─────────────────────────────── */}
                    <div className="flex items-center justify-between px-4 sm:px-6 py-4 shrink-0">
                        {/* Title + Counter */}
                        <div className="min-w-0 flex-1 mr-4">
                            {title && (
                                <h3 className="text-white text-sm sm:text-base font-semibold truncate">
                                    {title}
                                </h3>
                            )}
                            <p className="text-white/60 text-xs sm:text-sm mt-0.5">
                                {activeIndex + 1} / {count}
                            </p>
                        </div>

                        {/* Close button */}
                        <button
                            onClick={closeLightbox}
                            className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white"
                            aria-label="Close"
                        >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* ── Image Area ──────────────────────────── */}
                    <div
                        className="flex-1 min-h-0 relative flex items-center justify-center overflow-hidden select-none"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        {/* Left Arrow */}
                        {count > 1 && (
                            <button
                                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                                className="absolute left-2 sm:left-6 z-10 w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 backdrop-blur-sm transition-all text-white shadow-lg"
                                aria-label="Previous image"
                            >
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                        )}

                        {/* Current Image (with drag transform) */}
                        <div
                            className="w-full h-full flex items-center justify-center px-14 sm:px-20"
                            style={{
                                transform: `translateX(${currentOffset}px)`,
                                transition: isDragging.current ? "none" : "transform 0.3s ease",
                            }}
                        >
                            <img
                                key={activeIndex}
                                src={src(images[activeIndex])}
                                alt={title ? `${title} - Image ${activeIndex + 1}` : `Image ${activeIndex + 1}`}
                                className="max-w-full max-h-full object-contain rounded-lg"
                                draggable={false}
                                style={{ userSelect: "none" }}
                            />
                        </div>

                        {/* Right Arrow */}
                        {count > 1 && (
                            <button
                                onClick={(e) => { e.stopPropagation(); goNext(); }}
                                className="absolute right-2 sm:right-6 z-10 w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 backdrop-blur-sm transition-all text-white shadow-lg"
                                aria-label="Next image"
                            >
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        )}
                    </div>

                    {/* ── Thumbnail Strip ─────────────────────── */}
                    {count > 1 && (
                        <div className="shrink-0 px-4 py-3 overflow-x-auto">
                            <div className="flex items-center justify-center gap-2">
                                {images.map((img, i) => (
                                    <button
                                        key={i}
                                        onClick={() => { setActiveIndex(i); setCurrentOffset(0); }}
                                        className={`shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden border-2 transition-all ${
                                            i === activeIndex
                                                ? "border-white scale-110 shadow-lg shadow-white/20"
                                                : "border-transparent opacity-50 hover:opacity-80"
                                        }`}
                                    >
                                        <img
                                            src={src(img)}
                                            alt=""
                                            className="w-full h-full object-cover"
                                            draggable={false}
                                        />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}